import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { easeOut, panel } from "../motion";
import { IconChevron } from "./icons";

const pad = (n: number) => String(n).padStart(2, "0");
const range = (count: number) => Array.from({ length: count }, (_, i) => i);

const HOURS = range(24).map(pad);

export interface TimeSelectProps {
  id?: string;
  /** Mirrors the native aria-label attribute. */
  ariaLabel?: string;
  /** "HH:MM" */
  value: string;
  onChange: (value: string) => void;
  /** Minutes between consecutive minute options. Default 15. */
  minuteStep?: number;
  className?: string;
}

interface ColProps {
  label: string;
  options: string[];
  current: string;
  onPick: (option: string) => void;
}

/** One scrollable picker column; the current value is scrolled into view on open. */
function TimeCol({ label, options, current, onPick }: ColProps) {
  return (
    <div className="time-col" role="group" aria-label={label}>
      <span className="time-col-head">{label}</span>
      <div className="time-col-list">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            data-current={o === current ? "true" : undefined}
            className={`dropdown-item ${o === current ? "selected" : ""}`}
            onClick={() => onPick(o)}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Themed replacement for the native <input type="time">. Two side-by-side
 * columns (hours / minutes) inside a height-limited dropdown; picking either
 * column updates the value immediately so the other can still be adjusted
 * without reopening. Shares the .select-trigger / .dropdown-menu visual
 * language with <Select>.
 */
export function TimeSelect({ id, ariaLabel, value, onChange, minuteStep = 15, className }: TimeSelectProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fallbackId = useId();

  const [hour, minute] = value.split(":");

  const minutes = useMemo(() => {
    const out = range(Math.floor(60 / minuteStep)).map((i) => pad(i * minuteStep));
    // Always contain the current minute, even if it falls off the step grid.
    if (minute && !out.includes(minute)) {
      const at = out.findIndex((m) => m > minute);
      out.splice(at === -1 ? out.length : at, 0, minute);
    }
    return out;
  }, [minuteStep, minute]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // When the menu opens: anchor it in the viewport (fixed) just above the
  // trigger — always upward — and clamp it so the whole menu stays visible.
  // Absolute positioning inside the scrollable .content column would clip the
  // menu at the top/bottom edges; fixed escapes that. Also centre the current
  // value in each column (scrollIntoView would scroll the page behind it).
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const wrap = wrapRef.current;
    const menu = menuRef.current;
    if (!wrap || !menu) return;
    const trig = wrap.getBoundingClientRect();
    const menuH = menu.offsetHeight;
    const GUTTER = 8;
    const width = Math.min(Math.max(trig.width, 280), window.innerWidth - GUTTER * 2);
    const left = Math.max(GUTTER, Math.min(trig.left, window.innerWidth - width - GUTTER));
    const top = Math.max(GUTTER, trig.top - menuH - GUTTER);
    setMenuPos({ left, top, width });
    menu.querySelectorAll<HTMLElement>("[data-current]").forEach((el) => {
      const list = el.parentElement;
      if (list) list.scrollTop = el.offsetTop - (list.clientHeight - el.clientHeight) / 2;
    });
  }, [open]);

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
    }
  }

  const pick = (part: "hour" | "minute") => (option: string) =>
    onChange(part === "hour" ? `${option}:${minute}` : `${hour}:${option}`);


  return (
    <div className={`dropdown select-wrap ${className ?? ""}`} ref={wrapRef}>
      <button
        type="button"
        id={id ?? fallbackId}
        className="select-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span>{value}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: easeOut }}
          style={{ display: "inline-flex", marginLeft: "auto" }}
        >
          <IconChevron />
        </motion.span>
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            ref={menuRef}
            className="dropdown-menu time-select-menu"
            role="dialog"
            aria-label={ariaLabel}
            style={menuPos ? { position: "fixed", left: menuPos.left, top: menuPos.top, width: menuPos.width } : undefined}
            variants={panel}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <TimeCol label="hr" options={HOURS} current={hour ?? ""} onPick={pick("hour")} />
            <span className="time-col-sep" aria-hidden="true">:</span>
            <TimeCol label="min" options={minutes} current={minute ?? ""} onPick={pick("minute")} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

